<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  shouldBeSocialHandle,
  shouldBeLinkedInHandle,
} from 'shared/helpers/Validators';
import InputRadioGroup from './components/InputRadioGroup.vue';
import WidgetBuilderPreviewPane from './components/WidgetBuilderPreviewPane.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { WIDGET_BUILDER_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

export default {
  components: {
    InputRadioGroup,
    WidgetBuilderPreviewPane,
    NextButton,
    Editor,
  },
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isWidgetPreview: true,
      color: '#1f93ff',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      replyTime: 'in_a_few_minutes',
      avatarFile: null,
      avatarUrl: '',
      widgetBubblePosition: 'right',
      widgetBubbleLauncherTitle: this.$t(
        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.DEFAULT'
      ),
      widgetBubbleType: 'standard',
      widgetBubblePositions: [
        {
          id: 'left',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
          ),
          checked: false,
        },
        {
          id: 'right',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
          ),
          checked: true,
        },
      ],
      widgetBubbleTypes: [
        {
          id: 'standard',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
          ),
          checked: true,
        },
        {
          id: 'expanded_bubble',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
          ),
          checked: false,
        },
      ],
      welcomeTaglineEditorMenuOptions: WIDGET_BUILDER_EDITOR_MENU_OPTIONS,
      socialLinksLabel: '',
      socialInstagramHandle: '',
      socialFacebookHandle: '',
      socialTiktokHandle: '',
      socialYoutubeHandle: '',
      socialLinkedinHandle: '',
      socialTwitterHandle: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    storageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
    widgetScript() {
      const raw = this.inbox.web_widget_script;
      if (!raw || typeof raw !== 'string' || raw.length < 14) {
        return raw || '';
      }
      const options = {
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
      };
      return (
        raw.substring(0, 13) +
        this.$t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_SETTINGS', {
          options: JSON.stringify(options),
        }) +
        raw.substring(13)
      );
    },
    getWidgetViewOptions() {
      return [
        {
          id: 'preview',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
          ),
          checked: true,
        },
        {
          id: 'script',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
          ),
          checked: false,
        },
      ];
    },
    getReplyTimeOptions() {
      return [
        {
          key: 'in_a_few_minutes',
          value: 'in_a_few_minutes',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_MINUTES'
          ),
        },
        {
          key: 'in_a_few_hours',
          value: 'in_a_few_hours',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_HOURS'
          ),
        },
        {
          key: 'in_a_day',
          value: 'in_a_day',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_DAY'
          ),
        },
      ];
    },
    websiteNameValidationErrorMsg() {
      return this.v$.websiteName.$error
        ? this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.ERROR')
        : '';
    },
  },
  mounted() {
    this.setDefaults();
  },
  validations: {
    websiteName: { required },
    socialLinksLabel: {
      maxLen: value => !value || String(value).length <= 500,
    },
    socialInstagramHandle: { shouldBeSocialHandle },
    socialFacebookHandle: { shouldBeSocialHandle },
    socialTiktokHandle: { shouldBeSocialHandle },
    socialYoutubeHandle: { shouldBeSocialHandle },
    socialLinkedinHandle: { shouldBeLinkedInHandle },
    socialTwitterHandle: { shouldBeSocialHandle },
  },
  methods: {
    setDefaults() {
      // Widget Settings
      const {
        name,
        welcome_title,
        welcome_tagline,
        widget_color,
        reply_time,
        avatar_url,
      } = this.inbox;
      this.websiteName = name;
      this.welcomeHeading = welcome_title;
      this.welcomeTagline = welcome_tagline;
      this.color = widget_color;
      this.replyTime = reply_time;
      this.avatarUrl = avatar_url;

      const wh = this.inbox.widget_home_section_config || {};
      this.socialLinksLabel = wh.social_links_label || '';
      this.socialInstagramHandle =
        (wh.instagram || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.instagram_url, 'instagram');
      this.socialFacebookHandle =
        (wh.facebook || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.facebook_url, 'facebook');
      this.socialTiktokHandle =
        (wh.tiktok || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.tiktok_url, 'tiktok');
      this.socialYoutubeHandle =
        (wh.youtube || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.youtube_url, 'youtube');
      this.socialLinkedinHandle =
        (wh.linkedin || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.linkedin_url, 'linkedin');
      this.socialTwitterHandle =
        (wh.twitter || '').trim() ||
        this.handleFromLegacyProfileUrl(wh.twitter_url, 'twitter');

      const savedInformation = this.getSavedInboxInformation();
      if (savedInformation) {
        this.widgetBubblePositions = this.widgetBubblePositions.map(item => {
          if (item.id === savedInformation.position) {
            item.checked = true;
            this.widgetBubblePosition = item.id;
          }
          return item;
        });
        this.widgetBubbleTypes = this.widgetBubbleTypes.map(item => {
          if (item.id === savedInformation.type) {
            item.checked = true;
            this.widgetBubbleType = item.id;
          }
          return item;
        });
        this.widgetBubbleLauncherTitle =
          savedInformation.launcherTitle || 'Chat with us';
      }
    },
    handleWidgetBubblePositionChange(item) {
      this.widgetBubblePosition = item.id;
    },
    handleWidgetBubbleTypeChange(item) {
      this.widgetBubbleType = item.id;
    },
    handleWidgetViewChange(item) {
      this.isWidgetPreview = item.id === 'preview';
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('inboxes/deleteInboxAvatar', this.inbox.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async updateWidget() {
      const bubbleSettings = {
        position: this.widgetBubblePosition,
        launcherTitle: this.widgetBubbleLauncherTitle,
        type: this.widgetBubbleType,
      };

      LocalStorage.set(this.storageKey, bubbleSettings);

      try {
        const payload = {
          id: this.inbox.id,
          name: this.websiteName,
          channel: {
            widget_color: this.color,
            welcome_title: this.welcomeHeading,
            welcome_tagline: this.welcomeTagline,
            reply_time: this.replyTime,
            widget_home_section_config: {
              social_links_label: (this.socialLinksLabel || '').trim(),
              instagram: (this.socialInstagramHandle || '').trim(),
              facebook: (this.socialFacebookHandle || '').trim(),
              tiktok: (this.socialTiktokHandle || '').trim(),
              youtube: (this.socialYoutubeHandle || '').trim(),
              linkedin: (this.socialLinkedinHandle || '').trim(),
              twitter: (this.socialTwitterHandle || '').trim(),
            },
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
    getSavedInboxInformation() {
      return LocalStorage.get(this.storageKey);
    },
    handleFromLegacyProfileUrl(url, platform) {
      if (!url || typeof url !== 'string') return '';
      try {
        const u = new URL(url.trim());
        const parts = u.pathname.split('/').filter(Boolean);
        const first = parts[0] || '';
        const second = parts[1] || '';
        const host = u.hostname.replace(/^www\./, '');
        if (platform === 'instagram' && host.includes('instagram.com')) {
          return first;
        }
        if (platform === 'facebook' && host.includes('facebook.com')) {
          return first === 'profile' ? second : first;
        }
        if (platform === 'tiktok' && host.includes('tiktok.com')) {
          return first.startsWith('@') ? first.slice(1) : first;
        }
        if (
          platform === 'youtube' &&
          (host.includes('youtube.com') || host === 'youtu.be')
        ) {
          if (['channel', 'c', 'user'].includes(first)) return second || '';
          if (first.startsWith('@')) return first.slice(1);
          return first;
        }
        if (platform === 'linkedin' && host.includes('linkedin.com')) {
          if (first === 'in' && second) return second;
          if (first === 'company' && second) return `company/${second}`;
          return '';
        }
        if (
          platform === 'twitter' &&
          (host.includes('twitter.com') || host === 'x.com')
        ) {
          return first;
        }
      } catch {
        return '';
      }
      return '';
    },
  },
};
</script>

<template>
  <div
    class="box-border flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col px-8 pb-2"
  >
    <div
      class="flex w-full min-w-0 flex-col gap-6 overflow-visible p-2.5 lg:max-h-[calc(100vh-11rem)] lg:min-h-0 lg:flex-1 lg:flex-row lg:overflow-hidden"
    >
      <div
        class="w-full min-w-0 shrink-0 lg:flex lg:h-full lg:min-h-0 lg:w-[40%] lg:flex-col"
      >
        <div
          class="min-h-[20rem] py-4 px-px lg:h-full lg:max-h-full lg:min-h-0 lg:overflow-x-hidden lg:overflow-y-auto"
        >
          <form @submit.prevent="updateWidget">
            <woot-avatar-uploader
              :label="
                $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.LABEL')
              "
              :src="avatarUrl"
              delete-avatar
              @on-avatar-select="handleImageUpload"
              @on-avatar-delete="handleAvatarDelete"
            />
            <woot-input
              v-model="websiteName"
              :class="{ error: v$.websiteName.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.PLACE_HOLDER'
                )
              "
              :error="websiteNameValidationErrorMsg"
              @blur="v$.websiteName.$touch"
            />
            <woot-input
              v-model="welcomeHeading"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.PLACE_HOLDER'
                )
              "
            />
            <Editor
              v-model="welcomeTagline"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.PLACE_HOLDER'
                )
              "
              :max-length="255"
              :enabled-menu-options="welcomeTaglineEditorMenuOptions"
              class="mb-4"
            />
            <label>
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.LABEL')
              }}
              <select v-model="replyTime">
                <option
                  v-for="option in getReplyTimeOptions"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </label>
            <label>
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_COLOR_LABEL'
                )
              }}
              <woot-color-picker v-model="color" />
            </label>
            <p class="pb-2 pt-4 text-sm font-medium text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.TITLE'
                )
              }}
            </p>
            <p class="pb-3 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HELP'
                )
              }}
            </p>
            <woot-input
              v-model="socialLinksLabel"
              class="pb-4"
              :class="{ error: v$.socialLinksLabel.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.PREFIX_LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.PREFIX_PLACEHOLDER'
                )
              "
              :error="
                v$.socialLinksLabel.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.PREFIX_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialLinksLabel.$touch"
            />
            <p class="pb-4 text-xs not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.PREFIX_HELP'
                )
              }}
            </p>
            <woot-input
              v-model="socialInstagramHandle"
              class="pb-4"
              :class="{ error: v$.socialInstagramHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.INSTAGRAM.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.INSTAGRAM.PLACEHOLDER'
                )
              "
              :error="
                v$.socialInstagramHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HANDLE_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialInstagramHandle.$touch"
            />
            <woot-input
              v-model="socialFacebookHandle"
              class="pb-4"
              :class="{ error: v$.socialFacebookHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.FACEBOOK.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.FACEBOOK.PLACEHOLDER'
                )
              "
              :error="
                v$.socialFacebookHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HANDLE_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialFacebookHandle.$touch"
            />
            <woot-input
              v-model="socialTiktokHandle"
              class="pb-4"
              :class="{ error: v$.socialTiktokHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.TIKTOK.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.TIKTOK.PLACEHOLDER'
                )
              "
              :error="
                v$.socialTiktokHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HANDLE_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialTiktokHandle.$touch"
            />
            <woot-input
              v-model="socialYoutubeHandle"
              class="pb-4"
              :class="{ error: v$.socialYoutubeHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.YOUTUBE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.YOUTUBE.PLACEHOLDER'
                )
              "
              :error="
                v$.socialYoutubeHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HANDLE_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialYoutubeHandle.$touch"
            />
            <woot-input
              v-model="socialLinkedinHandle"
              class="pb-4"
              :class="{ error: v$.socialLinkedinHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.LINKEDIN.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.LINKEDIN.PLACEHOLDER'
                )
              "
              :error="
                v$.socialLinkedinHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.LINKEDIN_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialLinkedinHandle.$touch"
            />
            <woot-input
              v-model="socialTwitterHandle"
              class="pb-4"
              :class="{ error: v$.socialTwitterHandle.$error }"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.TWITTER.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.TWITTER.PLACEHOLDER'
                )
              "
              :error="
                v$.socialTwitterHandle.$error
                  ? $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.SOCIAL_HANDLES.HANDLE_ERROR'
                    )
                  : ''
              "
              @blur="v$.socialTwitterHandle.$touch"
            />
            <InputRadioGroup
              name="widget-bubble-position"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                )
              "
              :items="widgetBubblePositions"
              :action="handleWidgetBubblePositionChange"
            />
            <InputRadioGroup
              name="widget-bubble-type"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                )
              "
              :items="widgetBubbleTypes"
              :action="handleWidgetBubbleTypeChange"
            />
            <woot-input
              v-model="widgetBubbleLauncherTitle"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                )
              "
            />
            <NextButton
              type="submit"
              class="mt-4"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT'
                )
              "
              :is-loading="uiFlags.isUpdating"
              :disabled="v$.$invalid || uiFlags.isUpdating"
            />
          </form>
        </div>
      </div>
      <WidgetBuilderPreviewPane
        :is-widget-preview="isWidgetPreview"
        :widget-view-options="getWidgetViewOptions"
        :welcome-heading="welcomeHeading"
        :welcome-tagline="welcomeTagline"
        :website-name="websiteName"
        :avatar-url="avatarUrl"
        :reply-time="replyTime"
        :color="color"
        :widget-bubble-position="widgetBubblePosition"
        :widget-bubble-launcher-title="widgetBubbleLauncherTitle"
        :widget-bubble-type="widgetBubbleType"
        :widget-script="widgetScript"
        @widget-view-change="handleWidgetViewChange"
      />
    </div>
  </div>
</template>
